/**
 * Enrollment type definitions matching backend schemas exactly.
 * These types ensure type safety and consistency with the API.
 *
 * UPDATED: Reflects Invoice model separation from Enrollment.
 * Payment fields live on Invoice, not Enrollment.
 * Enrollment create/update only takes: student_id, class_template_id, session_id, status, notes.
 * Invoice data is handled separately via the invoice API or passed as invoice_data for creation.
 */
import { z } from "zod"

// Invoice data nested within enrollment (for creation)
export interface InvoiceData {
  invoice_number?: string | null
  amount_due: number
  amount_paid?: number
  payment_status?: "pending" | "partial" | "paid" | "refunded"
  payment_method?:
    | "credit_card"
    | "debit_card"
    | "check"
    | "cash"
    | "bank_transfer"
    | "other"
    | null
  payment_date?: string | null
  external_payment_id?: string | null
  notes?: string | null
}

// Invoice response from API
export interface InvoiceResponse {
  id: string
  enrollment_id: string
  invoice_number?: string | null
  amount_due: number
  amount_paid?: number
  payment_status?: "pending" | "partial" | "paid" | "refunded"
  payment_method?:
    | "credit_card"
    | "debit_card"
    | "check"
    | "cash"
    | "bank_transfer"
    | "other"
    | null
  payment_date?: string | null
  external_payment_id?: string | null
  notes?: string | null
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
}

// Base enrollment properties matching EnrollmentBase schema
export interface EnrollmentBase {
  student_id: string
  class_template_id: string
  session_id: string
  status?:
    | "pending"
    | "confirmed"
    | "waitlisted"
    | "cancelled"
    | "completed"
    | null
  notes?: string | null
}

// Create enrollment data matching EnrollmentCreate schema
// invoice_data is passed to the API for automatic invoice creation
export interface EnrollmentCreate extends EnrollmentBase {
  invoice_data?: InvoiceData
}

// Update enrollment data matching EnrollmentUpdate schema
export interface EnrollmentUpdate {
  student_id?: string
  class_template_id?: string
  session_id?: string
  status?:
    | "pending"
    | "confirmed"
    | "waitlisted"
    | "cancelled"
    | "completed"
    | null
  notes?: string | null
}

// Full enrollment response matching EnrollmentResponse schema
export interface EnrollmentPublic extends EnrollmentBase {
  id: string
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
  invoice?: InvoiceResponse | null // Nested invoice relationship
  waitlist_position?: number | null // Computed field
  effective_status?: string | null // Computed field
  // Display name computed fields (populated by backend enrichment)
  student_name?: string | null
  class_name?: string | null
  session_name?: string | null
}

// Response wrapper for API calls
export interface EnrollmentResponse extends EnrollmentPublic {}

// Paginated list response matching EnrollmentListResponse schema
export interface EnrollmentListResponse {
  items: EnrollmentResponse[]
  total: number
  page: number
  per_page: number
  pages: number
  meta: EnrollmentListMeta
}

export interface EnrollmentListMeta {
  sort_by: "created_at" | "updated_at"
  sort_order: "asc" | "desc"
  filters: EnrollmentFilters
}

// Form data interfaces for UI components
// Enrollment forms only handle enrollment fields; invoice is separate
export interface EnrollmentFormData {
  student_id?: string
  class_template_id?: string
  session_id?: string
  status?:
    | "pending"
    | "confirmed"
    | "waitlisted"
    | "cancelled"
    | "completed"
    | null
  notes?: string | null
}

export interface EnrollmentUpdateFormData {
  student_id?: string
  class_template_id?: string
  session_id?: string
  status?:
    | "pending"
    | "confirmed"
    | "waitlisted"
    | "cancelled"
    | "completed"
    | null
  notes?: string | null
}

// Filter interface for API queries
export interface EnrollmentFilters {
  limit?: number
  skip?: number
  search?: string
  sort_by?: "created_at" | "updated_at"
  sort_order?: "asc" | "desc"
  created_by?: string
  created_after?: string
  created_before?: string
  updated_after?: string
  updated_before?: string
}

// Component prop interfaces following sophisticated pattern
export interface AddEnrollmentProps {
  onSuccess?: () => void
  onCancel?: () => void
}

export interface EditEnrollmentProps {
  enrollment: EnrollmentResponse
  onSuccess?: () => void
  onCancel?: () => void
}

export interface DeleteEnrollmentProps {
  enrollment: EnrollmentResponse
  onSuccess?: () => void
  onCancel?: () => void
}

// Zod validation schema for invoice data (used when creating enrollment with invoice)
export const invoiceDataSchema = z.object({
  invoice_number: z.string().trim().max(50).nullable().optional(),
  amount_due: z.coerce.number().nonnegative(), // Allow 0 for auto-population from class template
  amount_paid: z.coerce.number().nonnegative().optional(),
  payment_status: z.enum(["pending", "partial", "paid", "refunded"]).optional(),
  payment_method: z
    .enum([
      "credit_card",
      "debit_card",
      "check",
      "cash",
      "bank_transfer",
      "other",
    ])
    .nullable()
    .optional(),
  payment_date: z.string().nullable().optional(),
  external_payment_id: z.string().trim().max(100).nullable().optional(),
  notes: z.string().trim().nullable().optional(),
})

// Zod validation schema for enrollment forms (no invoice_data -- invoice handled separately)
export const enrollmentFormSchema = z.object({
  student_id: z.string().trim().min(1, "Student is required"),
  class_template_id: z.string().trim().min(1, "Class template is required"),
  session_id: z.string().trim().min(1, "Session is required"),
  status: z
    .enum(["pending", "confirmed", "waitlisted", "cancelled", "completed"])
    .nullable()
    .optional(),
  notes: z.string().trim().nullable().optional(),
})

// Kept for backward compatibility -- same as enrollmentFormSchema
export const enrollmentCreateSchema = enrollmentFormSchema

export const enrollmentUpdateSchema = z.object({
  student_id: z.string().trim().max(32).optional(),
  class_template_id: z.string().trim().max(32).optional(),
  session_id: z.string().trim().max(32).optional(),
  status: z
    .enum(["pending", "confirmed", "waitlisted", "cancelled", "completed"])
    .nullable()
    .optional(),
  notes: z.string().trim().nullable().optional(),
})

// Type inference from Zod schemas
export type EnrollmentCreateInput = z.infer<typeof enrollmentCreateSchema>
export type EnrollmentUpdateInput = z.infer<typeof enrollmentUpdateSchema>
